import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, setIsOpen }) => {
  const { user } = useAuth();

  if (!user) return null;

  const churchIdentifier =
    import.meta.env.VITE_APP_CHURCH_IDENTIFIER?.replace('-', ' ').toUpperCase() || 'Your Church';

  const canAccessFinances = user.role === 'admin' || user.role === 'super-admin';
  const canManageAccounts = user.role === 'super-admin';

  const navLinkClass = (isActive: boolean) =>
    `flex items-center px-4 py-2 rounded-md transition-colors duration-200 ${
      isActive ? 'bg-gray-700 text-teal-300' : 'hover:bg-gray-700 text-gray-300'
    }`;

  const subNavLinkClass = (isActive: boolean) =>
    `flex items-center pl-8 pr-4 py-2 rounded-md transition-colors duration-200 text-sm ${
      isActive ? 'bg-gray-700 text-teal-300' : 'hover:bg-gray-700 text-gray-300'
    }`;

  const handleNavClick = () => {
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  return (
    <>
    {/* Mobile Menu Button */}
            <div className="lg:hidden fixed top-4 left-4 z-50">
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="text-white bg-gray-800 p-2 rounded-md shadow-lg"
                >
                    {isOpen ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    )}
                </button>
            </div>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed z-40 inset-y-0 left-0 transform bg-gray-800 text-white w-64 transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 lg:static lg:inset-auto lg:flex`}
      >
        <div className="flex flex-col h-full shadow-lg w-full">
          {/* Header */}
          <div className="p-6 border-b border-gray-700">
            <h2 className="text-2xl font-bold">ChMS</h2>
            <p className="text-sm text-gray-400 mt-1">{churchIdentifier}</p>
          </div>

          {/* User Info */}
          <div className="p-6 text-center border-b border-gray-700">
            <p className="text-xl font-semibold">{user.firstName} {user.lastName}</p>
            <p className="text-sm text-gray-400 capitalize">Role: {user.role}</p>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-2 overflow-y-auto">
            <NavLink to="/dashboard" end onClick={handleNavClick} className={({ isActive }) => navLinkClass(isActive)}>
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M22 11.414v12.586h-20v-12.586l-1.293 1.293-.707-.707 12-12 12 12-.707.707-1.293-1.293zm-6 11.586h5v-12.586l-9-9-9 9v12.586h5v-9h8v9zm-1-7.889h-6v7.778h6v-7.778z"/>
              </svg>
              Home
            </NavLink>

            {(user.role === 'admin' || user.role === 'super-admin') && (
              <NavLink to="/dashboard/members" onClick={handleNavClick} className={({ isActive }) => navLinkClass(isActive)}>
                <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.997 18h-.998c0-1.552.06-1.775-.88-1.993-1.438-.332-2.797-.645-3.293-1.729-.18-.396-.301-1.048.155-1.907 1.021-1.929 1.277-3.583.702-4.538-.672-1.115-2.707-1.12-3.385.017-.576.968-.316 2.613.713 4.512.465.856.348 1.51.168 1.908-.49 1.089-1.836 1.4-3.262 1.728-.982.227-.92.435-.92 2.002h-.995l-.002-.623c0-1.259.1-1.985 1.588-2.329 1.682-.389 3.344-.736 2.545-2.209-2.366-4.365-.676-6.839 1.865-6.839 2.492 0 4.227 2.383 1.867 6.839-.775 1.464.824 1.812 2.545 2.209 1.49.344 1.589 1.072 1.589 2.333l-.002.619z"/>
                </svg>
                Manage Members
              </NavLink>
            )}

            {canAccessFinances && (
              <>
                <NavLink to="/dashboard/finances" onClick={handleNavClick} className={({ isActive }) => navLinkClass(isActive)}>
                  <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22 24h-17c-1.657 0-3-1.343-3-3v-18c0-1.657 1.343-3 3-3h17v24zm-2-4h-14.505c-1.375 0-1.375 2 0 2h14.505v-2zm0-18h-15v16h15v-16zm-3 3v3h-9v-3h9z"/>
                  </svg>
                  Financial Records
                </NavLink>

                {canManageAccounts && (<>
                  {/* Manage Reports */}
                  
                  <NavLink to="/dashboard/reports" onClick={handleNavClick} className={({ isActive }) => subNavLinkClass(isActive)}>
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 8v12h-16v-12h16zm2-2h-20v16h20v-16zm-8 11.677v.323h-8v-.333c-.003-.89.035-1.398 1.059-1.634 1.122-.259 2.229-.491 1.697-1.473-1.577-2.911-.45-4.56 1.244-4.56 1.662 0 2.816 1.588 1.244 4.56-.517.976.551 1.208 1.697 1.473 1.028.237 1.063.748 1.059 1.644zm4-7.677h-3v2h3v-2zm0 3h-3v2h3v-2zm0 3h-3v2h3v-2zm1-14h-21v17h2v-15h19v-2z"/>
                    </svg>
                    Manage Reports
                  </NavLink>
                  
                  {/* Manage accounts */}
                  <NavLink to="/dashboard/accounts" onClick={handleNavClick} className={({ isActive }) => subNavLinkClass(isActive)}>
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M22 8v12h-16v-12h16zm2-2h-20v16h20v-16zm-8 11.677v.323h-8v-.333c-.003-.89.035-1.398 1.059-1.634 1.122-.259 2.229-.491 1.697-1.473-1.577-2.911-.45-4.56 1.244-4.56 1.662 0 2.816 1.588 1.244 4.56-.517.976.551 1.208 1.697 1.473 1.028.237 1.063.748 1.059 1.644zm4-7.677h-3v2h3v-2zm0 3h-3v2h3v-2zm0 3h-3v2h3v-2zm1-14h-21v17h2v-15h19v-2z"/>
                    </svg>
                    Manage Accounts
                  </NavLink>
                  </>
                )}
              </>
            )}

            <NavLink to="/dashboard/settings" onClick={handleNavClick} className={({ isActive }) => navLinkClass(isActive)}>
              <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 8.666c-1.838 0-3.333 1.496-3.333 3.334s1.495 3.333 3.333 3.333 3.333-1.495 3.333-3.333-1.495-3.334-3.333-3.334m0 7.667c-2.39 0-4.333-1.943-4.333-4.333s1.943-4.334 4.333-4.334 4.333 1.944 4.333 4.334c0 2.39-1.943 4.333-4.333 4.333"/>
              </svg>
              User Settings
            </NavLink>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Sidebar;

